import { AuthConsumerRenderProps, withAuthContext } from 'auth/AuthContext';
import FormContent from 'components/layout/FormContent';
import VirtualizedTable from 'components/layout/VirtualizedTable';
import LoaderComponent from 'components/LoaderComponent';
import React from 'react';
import { MessageSourceProps, withMessages } from 'react-message-source';
import { RouteComponentProps } from 'react-router';
import { Header } from 'semantic-ui-react';
import styled from 'styled-components';
import { InvoiceStatisticData } from 'ts-types/api.types';
import { isoToDisplayDate } from 'utils/dateUtils';
import { numberFormatter } from 'utils/fomattingUtils';
import { withRouterWorkaround } from 'utils/workaroundUtils';

const StyledTable = styled(VirtualizedTable)`
  .ReactVirtualized__Table__Grid {
    outline: none;
    border: 1px solid rgba(34, 36, 38, .15);
    border-top: none;
    border-radius: 0 0 .28571429rem .28571429rem;
  }

  .ReactVirtualized__Table__row {
    border-left: none;
  }

  .ReactVirtualized__Table__row.totalRow {
    font-weight: 700;
    background-color: rgba(0, 0, 0, .2);

    &:hover {
      background-color: rgba(0, 0, 0, .2) !important;
    }
  }

  .ReactVirtualized__Table__headerColumn {
    height: 100%;
    border-right: 1px solid rgba(34, 36, 38, .15);
    padding-top: 0.85rem;
  }

  .ReactVirtualized__Table__headerColumn:last-child {
    border-right: none;
  }

  .ReactVirtualized__Table__rowColumn {
    height: 100%;
    border-right: 1px solid rgba(34, 36, 38, .15);
    padding-top: 0.85rem;
  }

  .ReactVirtualized__Table__rowColumn.noBorder {
    border-right: none;
  }

  .ReactVirtualized__Table__rowColumn:last-child {
    border-right: none;
  }
`;

const StyledTotalsRowTable = styled(VirtualizedTable)`
  .ReactVirtualized__Table__Grid {
    display: none;
  }

  .ReactVirtualized__Table__row {
    border-left: none;
    border-radius: 0 0 .28571429rem .28571429rem;
  }

  .ReactVirtualized__Table__headerRow.headerRow {
    background-color: rgba(0, 0, 0, .2);
    border-top: none;
    border-radius: 0 0 .28571429rem .28571429rem;
  }

  .ReactVirtualized__Table__headerColumn {
    height: 100%;
    border-right: none;
    padding-top: 0.85rem;
    padding-right: 1rem;
  }

  .ReactVirtualized__Table__headerColumn:last-child {
    border-right: none;
  }

  .ReactVirtualized__Table__rowColumn {
    height: 100%;
    border-right: none;
    padding-top: 0.85rem;
  }

  .ReactVirtualized__Table__rowColumn.noBorder {
    border-right: none;
  }

  .ReactVirtualized__Table__rowColumn:last-child {
    border-right: none;
  }
`;

const TableContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  position: relative;
  max-height: 89px;
`;

const StyledInvoiceStatistic = styled.div`
  display: flex;
  flex-flow: column;
  height: 100%;

  .bill-list-form {
    display: flex;
    flex-direction: column;
  }
`;

const StyledFormContent = styled.div`
  flex: 1 1 auto;
  display: flex;
  flex-flow: row wrap;
  justify-content: space-around;
  margin-bottom: 15px;
`;

const StyledContainer = styled.div`

  flex: 1 1 auto;
  display: flex;
  flex-direction: column;

  .ui.header:not(h1):not(h2):not(h3):not(h4):not(h5):not(h6) {
    font-size: 1.1rem;
    color: #373737;

    &.small {
      font-size: 1.05em;
    }
  }

  &.margin {
    margin-left: 20px;
  }
`;

const StyledCell = styled.div`
  text-align: right;
  padding-right: 0.4rem;
`;

interface Props extends MessageSourceProps, AuthConsumerRenderProps, RouteComponentProps {
  invoiceStatisticData: InvoiceStatisticData[],
  fromDate: string,
  toDate: string,
  dataLoading: boolean,
  setErrorMessage: (errorMessage?: string) => void
}

const InvoiceStatisticTableSegment = (props: Props) => {

  const {invoiceStatisticData, fromDate, toDate, dataLoading, getMessage} = props;

  const amountCellRenderer = ({cellData}: any) => {
    if (cellData || cellData === 0) {
      return <StyledCell>{numberFormatter.format(cellData)}</StyledCell>
    }
  };

  const invoiceStatisticRowGetter = (invoiceStatistic: InvoiceStatisticData[]) => ({index}: any) => {

    if (invoiceStatistic[index]) {
      Object.assign(invoiceStatistic[index], {index: index + 1});

      return invoiceStatistic[index];
    }

    return {
      index: index + 1
    }
  };

  const invoiceStatisticRowRenderer = ({className, columns, index, key, style}: any) => {
    const a11yProps = {"aria-rowindex": index + 1};

    let styles = {...style, userSelect: "none"};

    return (
        <div
            {...a11yProps}
            className={className}
            key={key}
            role="row"
            style={styles}
        >
          {columns}
        </div>
    );
  };

  const capitalizeFirstLetter = (string: string) => {
    return string.charAt(0).toUpperCase() + string.slice(1);
  }

  const renderInvoiceStatisticTable = (): React.ReactNode => {

    const rowCount = invoiceStatisticData.length;

    const columnNames = ["byTreatmentDate", "openByTreatmentDate", "byBillDate", "byPaymentDate", "byStornoDate"];
    let columns: any[] = [];
    columnNames.forEach(columnName => {
      const column = {
        width: 400,
        label: getMessage(`invoiceStatistic.${columnName}`),
        dataKey: `total${capitalizeFirstLetter(columnName)}`,
        cellRenderer: amountCellRenderer
      }
      columns.push(column);
    })

    return (
        <StyledTable
            rowGetter={invoiceStatisticRowGetter(invoiceStatisticData)}
            rowRenderer={invoiceStatisticRowRenderer}
            rowCount={invoiceStatisticData.length > 0 ? rowCount : 0}
            columns={columns}
        />
    )
  };


  const title = getMessage("header.navigation.invoiceStatistic");
  const headerMessage = dataLoading || invoiceStatisticData.length <= 0
      ? title
      : `${title} (${isoToDisplayDate(fromDate)} - ${isoToDisplayDate(toDate)})`

    return (
        <StyledInvoiceStatistic>
          <FormContent className="bill-list-form">
            <StyledFormContent>
              <StyledContainer style={{width: "100%"}}>
                <Header>
                  {headerMessage}
                </Header>
                <TableContainer>
                  {dataLoading
                      ? <LoaderComponent
                          message={`${getMessage("loading")}
                          ${getMessage("header.navigation.invoiceStatistic").toLowerCase()}`}/>
                      : renderInvoiceStatisticTable()}
                </TableContainer>
              </StyledContainer>
            </StyledFormContent>
          </FormContent>
        </StyledInvoiceStatistic>
    );
}

export default withAuthContext(withRouterWorkaround(withMessages(InvoiceStatisticTableSegment)));
